import { BUDGET_SCENE_CAPTURE_ZOOM_FACTOR } from '@constants'
import { resolveSceneSeatHeightCmForDisplay } from '@utils/atemporal/seatHeightCatalog'
import {
  buildBudgetIllustrationsBlockHtml,
  fetchBudgetIllustrationsAsDataUrls,
  type BudgetIllustrationPack,
} from '@utils/budget/presupuestoIllustrations'
import { buildArtisanCharacteristicsHtml } from '@utils/budget/artisanCharacteristics'
import {
  buildFabricCharacteristicsHtml,
  fetchFabricCareIconsAsDataUrls,
  pickSelectedFabricTexture,
  resolveFabricPanelLabels,
  type FabricCareIconId,
} from '@utils/budget/fabricCharacteristics'
import { formatBudgetPieceDimensions, formatArmBudgetReference, formatCatalogBaseReference } from '@utils/budget/formatPieceDimensions'
import { getRelaxIntegratedArmBudgetContribution } from '@utils/atemporal/relaxArmVariants'
import { useAppStore } from '@store'
import reglaTriangularIcon from '@assets/nuevos-iconos/regla-triangular.png'
import eliminarIcon from '@assets/nuevos-iconos/Eliminar2.png'
import duplicarIcon from '@assets/sidebar/Duplicar.png'
import calculatorIcon from '@assets/nuevos-iconos/calculadora.png'
import atemporalLogoPresupuesto from '@assets/atemporal/AtemporalLogoPresupuesto.png'
import deshacerIcon from '@assets/atemporal/deshacer.png'
import rehacerIcon from '@assets/atemporal/rehacer.png'
// import disco from '@assets/topbar/disco1.png' // Guardar oculto temporalmente
import * as Tooltip from '@radix-ui/react-tooltip'
import * as Dialog from '@radix-ui/react-dialog'
import { useRef, useState } from 'react'
import { SpinnerPDFViewer } from '@components/PdfView/SpinnerPDFViewer'
import { useNavigate } from 'react-router-dom'
import { Store } from '@types'
import { ArmsCushionsPanel } from '@components/ArmsCushionsPanel'
import { ArmRestrictionNoticeModal } from '@components/ArmsCushionsPanel/ArmRestrictionNoticeModal'
import { BudgetPreviewModal } from '@components/Budget/BudgetPreviewModal'
import { useUndoRedo } from '@hooks/useUndoRedo'
import { useFetchMenu } from '@hooks/useFetchMenu'
// import { useUser } from '@context/user' // Guardar oculto temporalmente
import { usePreciosHubContext } from '@context/PreciosHubContext'
import {
  duplicateSelectedLinkedUnitAtRightEnd,
  resolveSelectedLinkedUnit,
} from '@utils/sceneObjects/items/duplicateLinkedUnit'
import { SelectedUnitInfo } from '@components/Viewer3D/SelectedUnitInfo'
import agregarDocumento from '@assets/nuevos-iconos/agregarIcon.png'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { ui } from '@utils/i18n/uiCopy'
import { uiDateLocale } from '@utils/i18n/uiLocale'

export function DimensionsHeader() {
  // const { user } = useUser() // Guardar oculto temporalmente
  const { resultadoPrecios } = usePreciosHubContext()

    const hideDimentions3D = useAppStore((s) => s.hideDimentions3D)
  const setHideDimentions3D = useAppStore((s) => s.setHideDimentions3D)
  const deleteSelectedObjects = useAppStore.use.deleteSelectedObjects()
  const currentLayer = useAppStore.use.currentLayer()
  const layers = useAppStore.use.layers()
  const items = useAppStore.use.items()
  const selectedItems = layers[currentLayer]?.selected || new Set()
  const currentObjectID = useAppStore.use.currentObjectID()
  const mode = useAppStore.use.mode()
  const setMode = useAppStore.use.setMode()
  const isProjectOpen = useAppStore((s) => s.isProjectOpened)
  const navigate = useNavigate()
  // const openModal = useAppStore((s) => s.openModal) // solo se usaba al abrir modal de nuevo diseño
  // const esMio = useAppStore((s) => s.esMio) // Guardar oculto temporalmente
  // const tipoUsuario = useAppStore((s) => s.tipoUsuario)
  const { canUndo, canRedo, performUndo, performRedo } = useUndoRedo()

  const [showBudgetPreview, setShowBudgetPreview] = useState(false)
  const [capturedSceneImage, setCapturedSceneImage] = useState('')
  const [isPreparingBudget, setIsPreparingBudget] = useState(false)
  const [isDuplicating, setIsDuplicating] = useState(false)
  const budgetContentRef = useRef<HTMLDivElement>(null)
  const canDuplicateUnit = Boolean(
    resolveSelectedLinkedUnit(items, selectedItems, currentObjectID),
  )

  const normalizeBudgetReference = (item: any) => {
    const rawReference = String(item?.referencia || item?.catalogID || '-')
    if (item?.type !== 'linkedItem') return rawReference
    return formatCatalogBaseReference(rawReference, rawReference)
  }
  const isSeatLinkedSon = (item: any) =>
    item?.type === 'linkedSon' &&
    String(item?.catalogID || item?.referencia || '')
      .toLowerCase()
      .includes('asiento')
  const isArmLinkedItem = (item: any) => {
    if (item?.type !== 'linkedItem') return false
    const reference = String(item?.catalogID || item?.referencia || '').trim()
    return /^BR\d+(?:-\d+)?$/i.test(reference)
  }
  const getCushionBudgetLabel = (catalog: string) => {
    const normalized = String(catalog || '').toLowerCase()
    if (normalized.includes('cojinesr')) return ui('rectangularCushions')
    if (normalized.includes('cojin')) return ui('multiCushions')
    return ''
  }

  const budgetPieces = Array.from(items.values())
    .filter(
      (item: any) =>
        item?.type === 'linkedItem' && item?.universal === false && !isArmLinkedItem(item),
    )
    .map((item: any) => ({
      id: String(item?.id || ''),
      referencia: normalizeBudgetReference(item),
      thickness: Number(item?.thickness || 0),
      height: Number(item?.height || 0),
      width: Number(item?.width || 0),
    }))
  const budgetArms = (() => {
    const grouped = new Map<
      string,
      { id: string; count: number; thickness: number; height: number; width: number }
    >()

    const addArm = (
      armRef: string,
      count: number,
      dims?: { thickness?: number; height?: number; width?: number },
    ) => {
      const id = formatArmBudgetReference(armRef)
      if (!id || count <= 0) return
      const existing = grouped.get(id)
      if (existing) {
        existing.count += count
        return
      }
      grouped.set(id, {
        id,
        count,
        thickness: Number(dims?.thickness || 0),
        height: Number(dims?.height || 0),
        width: Number(dims?.width || 0),
      })
    }

    Array.from(items.values()).forEach((item: any) => {
      if (item?.type !== 'linkedItem' || item?.universal !== false) return

      if (isArmLinkedItem(item)) {
        addArm(String(item?.referencia || item?.catalogID || ''), 1, item)
        return
      }

      // Relax con BRx I/D → +1; BRx A → +2; sin brazo → nada.
      const relaxArms = getRelaxIntegratedArmBudgetContribution(item?.catalogID || item?.referencia)
      if (relaxArms) addArm(relaxArms.armRef, relaxArms.count)
    })

    return Array.from(grouped.values())
  })()

  const seatHeightCm = resolveSceneSeatHeightCmForDisplay(items)
  const seatChildrenCount = Array.from(items.values()).filter((item: any) => {
    if (!isSeatLinkedSon(item)) return false
    return Boolean(item?.firstParentID)
  }).length
  const seatStitchType = (() => {
    const source = Array.from(items.values()).find(
      (item: any) => item?.type === 'linkedItem' && String(item?.seatStitchType || '').trim(),
    ) as any
    const stitch = String(source?.seatStitchType || '').trim().toLowerCase()
    return stitch === 'sin' ? 'sin' : 'con'
  })()
  const seatCountLabel = seatChildrenCount === 1 ? 'asiento' : 'asientos'
  const seatStitchLabel = seatStitchType === 'sin' ? 'sin costura' : 'con costura'
  const sceneCushionLabel = (() => {
    const cushionSon = Array.from(items.values()).find((item: any) => {
      if (item?.type !== 'linkedSon') return false
      const catalog = String(item?.catalogID || item?.referencia || '').toLowerCase()
      return catalog.includes('cojin')
    }) as any
    return getCushionBudgetLabel(String(cushionSon?.catalogID || cushionSon?.referencia || ''))
  })()

  const selectedParentLinkedItem = (() => {
    const selectedLinkedParent = Array.from(items.values()).find(
      (item: any) => item?.type === 'linkedItem' && item?.universal === false && item?.selected,
    ) as any
    if (selectedLinkedParent) return selectedLinkedParent

    const primaryPackParent = Array.from(items.values()).find(
      (item: any) =>
        item?.type === 'linkedItem' &&
        item?.universal === false &&
        item?.packPrimaryId &&
        item?.packPrimaryId === item?.id,
    ) as any
    if (primaryPackParent) return primaryPackParent

    return Array.from(items.values()).find(
      (item: any) => item?.type === 'linkedItem' && item?.universal === false,
    ) as any
  })()

  const selectedFabricTexture = (() => {
    const textures = Array.isArray(selectedParentLinkedItem?.textures)
      ? selectedParentLinkedItem.textures
      : []
    return pickSelectedFabricTexture(textures)
  })()

  const fabricInfo = {
    acabado: String(selectedFabricTexture?.acabado || ''),
    name: String(selectedFabricTexture?.name || ''),
    imageUrl: String(selectedFabricTexture?.url || ''),
  }
  const fabricLabels = resolveFabricPanelLabels(selectedFabricTexture as any)
  const fabricCharacteristics = fabricLabels.profile
  const fabricMiniImageUrl = (() => {
    const zoneName = String(selectedFabricTexture?.zoneName || '')
    const zoneMaterial = zoneName ? (selectedParentLinkedItem as any)?.zones?.[zoneName]?.material : null
    const miniName = String(zoneMaterial?.img || '').trim()
    const materialUrl = String(zoneMaterial?.url || fabricInfo.imageUrl || '').trim()
    if (!miniName || !materialUrl) return ''
    const lastSlash = materialUrl.lastIndexOf('/')
    if (lastSlash < 0) return ''
    const basePath = materialUrl.slice(0, lastSlash + 1)
    return `${basePath}${miniName}`
  })()
  const fabricSwatchSourceUrl = fabricInfo.imageUrl || fabricMiniImageUrl
  const safeFabricImageUrl = fabricSwatchSourceUrl ? encodeURI(fabricSwatchSourceUrl) : ''
  const safeFabricFallbackImageUrl = fabricMiniImageUrl ? encodeURI(fabricMiniImageUrl) : ''
  const livePrecioTotal =
    resultadoPrecios && typeof resultadoPrecios.precioTotal === 'number'
      ? resultadoPrecios.precioTotal
      : null
  const formattedPrecioTotal =
    livePrecioTotal !== null
      ? `${new Intl.NumberFormat('es-ES').format(livePrecioTotal)} €`
      : '-'

  // Obtener datos del menú para el panel de brazos
  const { menuData: armsMenuData } = useFetchMenu('PRINCIPAL', 'PRINCIPAL', null)
  const { menuData: relaxMenuData } = useFetchMenu('RELAXMENU', 'RELAXMENU', null)

  const toggleDimensions = () => {
    if (setHideDimentions3D) {
      setHideDimentions3D(!hideDimentions3D)
    }
  }

  const handleDelete = () => {
    if (selectedItems.size > 0) {
      deleteSelectedObjects()
    }
  }

  const handleDuplicate = async () => {
    if (!canDuplicateUnit || isDuplicating) return
    setIsDuplicating(true)
    try {
      await duplicateSelectedLinkedUnitAtRightEnd()
    } catch (error) {
      console.error('No se pudo duplicar la unidad:', error)
    } finally {
      setIsDuplicating(false)
    }
  }

  const handleNewDesign = () => {
    if (mode === 'MODE_3D_VIEW') setMode('MODE_IDLE')
    useAppStore.getState().setFrontalActive?.(false)
    // Sin modal: NewDesign ejecuta "No" (nuevo en blanco) al navegar.
    navigate('/planner/newDesign')
  }

  // Guardar oculto temporalmente
  // const handleSaveProject = (e: React.MouseEvent) => {
  //   e.preventDefault()
  //   if (user?.bloquearEdicion === true && tipoUsuario !== 'G') return
  //   if (!esMio && tipoUsuario !== 'G') return
  //   useAppStore.getState().setFrontalActive?.(false)
  //   const isUserLogged = window.localStorage.getItem('isUserLogged')
  //   sessionStorage.setItem('pendingSave', 'true')
  //   localStorage.setItem('pendingAction', 'saveProject')
  //   localStorage.setItem('pendingFrom', 'guardar')
  //   setTimeout(() => {
  //     if (!isUserLogged || tipoUsuario === 'P') {
  //       navigate('/planner/login')
  //     } else {
  //       navigate('/planner/saveProject')
  //     }
  //   }, 0)
  // }

  const getLogoDataUrl = async () => {
    try {
      const response = await fetch(atemporalLogoPresupuesto)
      const blob = await response.blob()
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result)
          } else {
            resolve(atemporalLogoPresupuesto)
          }
        }
        reader.onerror = () => reject(new Error('No se pudo convertir el logo'))
        reader.readAsDataURL(blob)
      })
    } catch {
      return atemporalLogoPresupuesto
    }
  }

  const getImageDataUrl = async (imageUrl: string) => {
    if (!imageUrl) return ''
    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result)
          } else {
            resolve('')
          }
        }
        reader.onerror = () => reject(new Error('No se pudo convertir imagen'))
        reader.readAsDataURL(blob)
      })
    } catch {
      return ''
    }
  }

  const buildBudgetHtml = (
    logoSrc: string,
    illus: BudgetIllustrationPack,
    fabricCareIconDataUrls: Partial<Record<FabricCareIconId, string>> = {},
  ) => {
    const today = new Date().toLocaleDateString(uiDateLocale())
    const escapeHtml = (value: string) =>
      value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;')

    const imageBlock = capturedSceneImage
      ? `<img src="${capturedSceneImage}" alt="${escapeHtml(ui('currentScene'))}" style="width:100%;height:auto;border-radius:12px;border:1px solid #d8d8d8;" />`
      : `<div style="height:240px;border:1px dashed #d8d8d8;border-radius:12px;display:flex;align-items:center;justify-content:center;color:#6d6d6d;">${escapeHtml(ui('noCapture'))}</div>`
    const piecesHtml = budgetPieces
      .map(
        (piece) =>
          `<div class="line">${escapeHtml(ui('refPrefix'))} ${escapeHtml(piece.referencia)} ${formatBudgetPieceDimensions(piece)}</div>`,
      )
      .join('')
    const armsHtml = budgetArms
      .map(
        (arm) =>
          `<div class="line">${arm.count} ${
            arm.count === 1 ? 'brazo' : 'brazos'
          } - <span class="label-strong">Ref:</span> ${escapeHtml(arm.id)}</div>`,
      )
      .join('')
    const budgetIllustrationsHtml = buildBudgetIllustrationsBlockHtml(escapeHtml, {
      sceneCushionLabel: sceneCushionLabel || '',
      seatHeightCm,
      seatCountLabel,
      seatStitchLabel,
      illus,
    })
    const fabricCareHtml = fabricCharacteristics
      ? buildFabricCharacteristicsHtml(escapeHtml, fabricCharacteristics, fabricCareIconDataUrls)
      : ''
    const tejidoHtml =
      fabricLabels.group || fabricLabels.subgroup || fabricLabels.textureLabel || safeFabricImageUrl
      ? `<div class="line"><span class="label-strong">${escapeHtml(ui('fabric'))}:</span></div>
    <div class="fabric-head">
        ${
          safeFabricImageUrl
            ? `<span class="fabric-swatch" style="background-image:url('${safeFabricImageUrl}');"></span>`
            : `<span class="fabric-dot"></span>`
        }
        <div>
          ${fabricLabels.group ? `<div class="fabric-title">${escapeHtml(fabricLabels.group)}</div>` : ''}
          ${fabricLabels.subgroup ? `<div class="fabric-subtitle">${escapeHtml(fabricLabels.subgroup)}</div>` : ''}
          ${fabricLabels.textureLabel ? `<div class="fabric-code">${escapeHtml(fabricLabels.textureLabel)}</div>` : ''}
        </div>
    </div>
    ${fabricCareHtml}`
      : `<div class="line">${escapeHtml(ui('fabric'))} <span class="muted">${escapeHtml(ui('withPhoto'))}</span></div>`

    return `<!doctype html>
<html lang="${uiDateLocale() === 'es-ES' ? 'es' : 'en'}">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(ui('budget'))}</title>
  <style>
    @page { size: A4; margin: 8mm; }
    @media print {
      html, body { margin: 0 !important; padding: 0 !important; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .budget-options-row { gap: 14px !important; }
      .budget-opt-card { max-width: 220px !important; min-width: 130px !important; flex: 1 1 150px !important; }
      .budget-opt-img-wrap { height: 166px !important; }
      .budget-opt-caption { font-size: 15px !important; margin-top: 8px !important; }
    }
    body { font-family: Arial, sans-serif; margin: 0; padding: 32px; color: #222; }
    .sheet { max-width: 840px; margin: 0 auto; }
    .logo { width: 220px; margin-bottom: 36px; }
    .line { margin-bottom: 14px; font-size: 22px; }
    .label-strong { font-weight: 700; }
    .muted { color: #8a8a8a; }
    .block { margin-top: 24px; }
    .total { margin-top: 22px; font-size: 22px; font-weight: 700; }
    .preview { margin-top: 28px; }
    .fabric-head { display: flex; align-items: flex-start; gap: 10px; margin: 4px 0 12px; }
    .fabric-dot { display: inline-block; width: 200px; height: 80px; border-radius: 10px; margin-top: 0; background: #b08f2f; flex-shrink: 0; border: 1px solid #d4d4d4; }
    .fabric-swatch { display: inline-block; width: 200px; height: 80px; border-radius: 10px; margin-top: 0; background-size: cover; background-position: center; flex-shrink: 0; border: 1px solid #d4d4d4; }
    .fabric-title { font-size: 15px; font-weight: 700; margin-bottom: 2px; }
    .fabric-subtitle { font-size: 12px; color: #6d6d6d; }
    .fabric-code { font-size: 12px; color: #8a8a8a; margin-top: 2px; }
    .budget-characteristics-block { margin-bottom: 8px; }
    .budget-options-wrap { margin: 4px 0 24px; }
    .budget-options-row { display: flex; flex-wrap: wrap; gap: 24px; align-items: stretch; justify-content: flex-start; }
    .budget-opt-card { flex: 1 1 220px; max-width: 300px; min-width: 180px; text-align: center; display: flex; flex-direction: column; align-items: center; }
    .budget-opt-img-wrap { width: 100%; height: 220px; display: block; position: relative; background: #f0f0f0; border-radius: 16px; border: 1px solid #cfcfcf; box-shadow: 0 6px 18px rgba(0,0,0,0.08); box-sizing: border-box; overflow: hidden; }
    .budget-opt-img-wrap img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
    .budget-opt-caption { margin-top: 14px; font-size: 18px; font-weight: 700; color: #1a1a1a; line-height: 1.35; }
    .characteristics-block { margin: 8px 0 20px; }
    .characteristics-text { margin: 0 0 4px; font-size: 16px; line-height: 1.35; color: #2a2a2a; }
    .fabric-care-block { margin: 10px 0 16px; }
    .fabric-care-title { font-size: 15px; font-weight: 700; margin-bottom: 4px; }
    .fabric-care-group { font-size: 12px; font-weight: 700; margin: 4px 0 2px; color: #2a2a2a; }
    .fabric-care-desc { font-size: 14px; line-height: 1.45; color: #2a2a2a; margin-bottom: 8px; }
    .fabric-care-icons { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
    .fabric-care-icon { width: 28px; height: 28px; object-fit: contain; }
    .fabric-care-icon--slightly-small { width: 24px; height: 24px; }
    .fabric-care-icon--slightly-large { width: 32px; height: 32px; }
    .fabric-care-icon--medium { width: 36px; height: 36px; }
    .fabric-care-icon--large { width: 36px; height: 36px; }
  </style>
</head>
<body>
  <main class="sheet">
    <img class="logo" src="${logoSrc}" alt="Atemporal" />
    <div class="line"><span class="label-strong">${escapeHtml(ui('date'))}:</span> ${today}</div>
    <div class="line"><span class="label-strong">${escapeHtml(ui('model'))}:</span> Artisan</div>
    ${buildArtisanCharacteristicsHtml(escapeHtml)}
    <div class="line"><span class="label-strong">${escapeHtml(ui('reference'))}:</span></div>
    ${piecesHtml}
    ${budgetArms.length > 0 ? `<div class="line"><span class="label-strong">Brazos:</span></div>${armsHtml}` : ''}
    ${budgetIllustrationsHtml}
    ${tejidoHtml}
    <div class="preview">${imageBlock}</div>
    <div class="total"><span class="label-strong">Precio Total:</span> ${formattedPrecioTotal}</div>
  </main>
</body>
</html>`
  }

  const handleDownloadBudget = async () => {
    const target = budgetContentRef.current
    if (!target) return

    const cloneWrapper = document.createElement('div')
    cloneWrapper.style.position = 'fixed'
    cloneWrapper.style.left = '-100000px'
    cloneWrapper.style.top = '0'
    cloneWrapper.style.width = '1024px'
    cloneWrapper.style.background = '#ffffff'
    cloneWrapper.style.padding = '24px'
    cloneWrapper.style.zIndex = '-1'

    const clonedContent = target.cloneNode(true) as HTMLElement
    const clonedActions = clonedContent.querySelector('[data-budget-actions]') as HTMLElement | null
    if (clonedActions) clonedActions.style.display = 'none'

    const clonedFabricSwatchImg = clonedContent.querySelector(
      '[data-fabric-swatch-img]',
    ) as HTMLImageElement | null
    if (clonedFabricSwatchImg && safeFabricImageUrl) {
      const fabricDataUrl = await getImageDataUrl(safeFabricImageUrl)
      if (fabricDataUrl) {
        clonedFabricSwatchImg.src = fabricDataUrl
      }
    }

    cloneWrapper.appendChild(clonedContent)
    document.body.appendChild(cloneWrapper)

    const clonedImages = Array.from(cloneWrapper.querySelectorAll('img')) as HTMLImageElement[]
    await Promise.all(
      clonedImages.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) {
              resolve()
              return
            }
            img.onload = () => resolve()
            img.onerror = () => resolve()
          }),
      ),
    )

    const captureCanvas = await html2canvas(cloneWrapper, {
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      scale: Math.min(window.devicePixelRatio || 1, 2),
    })
    cloneWrapper.remove()

    const imgData = captureCanvas.toDataURL('image/png')
    const pdf = new jsPDF('p', 'mm', 'a4')
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const margin = 8
    const usableWidth = pageWidth - margin * 2
    const usableHeight = pageHeight - margin * 2
    const widthScale = usableWidth / captureCanvas.width
    const heightScale = usableHeight / captureCanvas.height
    const scale = Math.min(widthScale, heightScale)
    const renderWidth = captureCanvas.width * scale
    const renderHeight = captureCanvas.height * scale
    const offsetX = margin + (usableWidth - renderWidth) / 2
    const offsetY = margin

    // Ajuste a una sola página, alineado arriba para parecerse a "Imprimir".
    pdf.addImage(imgData, 'PNG', offsetX, offsetY, renderWidth, renderHeight)

    pdf.save(`presupuesto-${new Date().toISOString().slice(0, 10)}.pdf`)
  }

  const handlePrintBudget = async () => {
    const logoSrc = await getLogoDataUrl()
    const illus = await fetchBudgetIllustrationsAsDataUrls({
      sceneCushionLabel: sceneCushionLabel || '',
      seatHeightCm,
      seatStitchType,
    })
    const fabricCareIcons = await fetchFabricCareIconsAsDataUrls(fabricCharacteristics)
    const html = buildBudgetHtml(logoSrc, illus, fabricCareIcons)

    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    document.body.appendChild(iframe)

    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus()
        iframe.contentWindow?.print()
      } finally {
        setTimeout(() => {
          iframe.remove()
        }, 1200)
      }
    }
    iframe.srcdoc = html
  }

  const captureSceneWithOverlays = async () => {
    const storeBeforeCapture = useAppStore.getState()
    const previousHideDimensions = storeBeforeCapture.hideDimentions3D ?? false
    const previousCurrentLayer = storeBeforeCapture.currentLayer
    const previousCurrentObjectId = storeBeforeCapture.currentObjectID
    const previousSelected = Array.from(storeBeforeCapture.layers[previousCurrentLayer]?.selected || [])

    // Ocultar cotas y gizmo (deseleccionando temporalmente) para la captura del presupuesto.
    useAppStore.setState((state) => {
      state.hideDimentions3D = true
      state.currentObjectID = ''
      state.layers[state.currentLayer]?.selected?.clear()
    })

    await new Promise((resolve) => requestAnimationFrame(resolve))
    await new Promise((resolve) => requestAnimationFrame(resolve))
    await new Promise((resolve) => setTimeout(resolve, 80))

    try {
      const sceneCanvas = document.querySelector('canvas') as HTMLCanvasElement | null
      if (!sceneCanvas) return ''

      const targetElement = (sceneCanvas.parentElement as HTMLElement | null) ?? sceneCanvas
      const screenshotCanvas = await html2canvas(targetElement, {
        useCORS: true,
        backgroundColor: null,
        logging: false,
        scale: Math.min(window.devicePixelRatio || 1, 2),
      })

      const zoomFactor = Math.max(1, BUDGET_SCENE_CAPTURE_ZOOM_FACTOR)
      const cropWidth = screenshotCanvas.width / zoomFactor
      const cropHeight = screenshotCanvas.height / zoomFactor
      const cropX = (screenshotCanvas.width - cropWidth) / 2
      const cropY = (screenshotCanvas.height - cropHeight) / 2

      const croppedCanvas = document.createElement('canvas')
      croppedCanvas.width = screenshotCanvas.width
      croppedCanvas.height = screenshotCanvas.height
      const ctx = croppedCanvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(
          screenshotCanvas,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          0,
          0,
          croppedCanvas.width,
          croppedCanvas.height,
        )
      }

      const fullCaptureData = croppedCanvas.toDataURL('image/png')
      if (fullCaptureData.length > 100) {
        return fullCaptureData
      }
    } catch (error) {
      console.error('No se pudo capturar escena con overlays:', error)
    } finally {
      // Restaurar estado de cotas/selección para no afectar la edición.
      useAppStore.setState((state) => {
        state.hideDimentions3D = previousHideDimensions
        state.currentObjectID = previousCurrentObjectId
        if (state.layers[previousCurrentLayer]) {
          state.layers[previousCurrentLayer].selected.clear()
          previousSelected.forEach((id) => state.layers[previousCurrentLayer].selected.add(id))
        }
      })
    }
    return ''
  }

  const handlePresupuestoClick = async () => {
    setIsPreparingBudget(true)
    try {
      setShowBudgetPreview(true)
    } finally {
      setIsPreparingBudget(false)
    }
  }

  const hasSelectedItems = selectedItems.size > 0

  return (
    <>
      {/* Banda del viewport: left = sidebar (20rem) + gap; right = panel telas + gap. Simétrico en todos los anchos. */}
      <div className='pointer-events-none fixed top-20 z-[40] flex items-start justify-between gap-3 left-3 right-[calc(0.5rem+clamp(240px,26vw,320px))] min-[1200px]:left-[calc(20rem+0.5rem)] min-[1200px]:right-[calc(0.5rem+clamp(240px,26vw,320px))]'>
        <SelectedUnitInfo
          items={items}
          selectedItems={selectedItems}
          currentObjectID={currentObjectID}
        />
        <div className='pointer-events-auto ml-auto shrink-0'>
          <div className='border-colorAtemporal flex items-center justify-center gap-2 rounded-2xl border bg-white px-3 py-2 shadow-lg min-[480px]:px-4 min-[480px]:py-3 min-[2400px]:gap-4 min-[2400px]:rounded-3xl min-[2400px]:px-7 min-[2400px]:py-5'>
            {/* Guardar: oculto temporalmente (producto no lo necesita ahora)
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    onClick={handleSaveProject}
                    className='border-colorAtemporal flex h-12 w-12 items-center justify-center rounded-xl border-[1px] bg-C/Color1 text-C/Color5 transition-all hover:bg-C/Color2 min-[2400px]:h-16 min-[2400px]:w-16 min-[2400px]:rounded-2xl'
                    aria-label={ui('saveProject')}
                  >
                    <img
                      src={disco}
                      alt={ui('saveProject')}
                      className='pointer-events-none h-6 w-6 select-none min-[2400px]:h-8 min-[2400px]:w-8'
                      draggable={false}
                    />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Content
                  side='bottom'
                  align='center'
                  className='z-50 rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-C/Color5 outline outline-[0.5px] outline-C/Color3'
                >
                  {ui('saveProject')}
                  <Tooltip.Arrow className='fill-C/Color1' />
                </Tooltip.Content>
              </Tooltip.Root>
            </Tooltip.Provider>
            */}

            {/* Botón de nuevo diseño */}
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    onClick={handleNewDesign}
                    className='border-colorAtemporal flex h-12 w-12 items-center justify-center rounded-xl border-[1px] bg-C/Color1 text-C/Color5 transition-all hover:bg-C/Color2 min-[2400px]:h-16 min-[2400px]:w-16 min-[2400px]:rounded-2xl'
                    aria-label={ui('createNewProject')}
                  >
                    <img
                      src={agregarDocumento}
                      alt={ui('createNewProject')}
                      className='pointer-events-none h-6 w-6 select-none min-[2400px]:h-8 min-[2400px]:w-8'
                      draggable={false}
                    />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Content
                  side='bottom'
                  align='center'
                  className='z-50 rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-C/Color5 outline outline-[0.5px] outline-C/Color3'
                >
                  {ui('createNewProject')}
                  <Tooltip.Arrow className='fill-C/Color1' />
                </Tooltip.Content>
              </Tooltip.Root>
            </Tooltip.Provider>

            {/* Botón de presupuesto */}
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    onClick={handlePresupuestoClick}
                    className='border-colorAtemporal flex h-12 w-12 items-center justify-center rounded-xl border-[1px] bg-C/Color1 text-C/Color5 transition-all hover:bg-C/Color2 min-[2400px]:h-16 min-[2400px]:w-16 min-[2400px]:rounded-2xl'
                    aria-label={ui('budget')}
                  >
                    <img
                      src={calculatorIcon}
                      alt={ui('budget')}
                      className='pointer-events-none h-6 w-6 select-none min-[2400px]:h-8 min-[2400px]:w-8'
                      draggable={false}
                    />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Content
                  side='bottom'
                  align='center'
                  className='z-50 rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-C/Color5 outline outline-[0.5px] outline-C/Color3'
                >
                  {ui('budget')}
                  <Tooltip.Arrow className='fill-C/Color1' />
                </Tooltip.Content>
              </Tooltip.Root>
            </Tooltip.Provider>

            {/* Deshacer */}
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    type='button'
                    onClick={performUndo}
                    disabled={!canUndo}
                    className={`border-colorAtemporal flex h-12 w-12 items-center justify-center rounded-xl border-[1px] transition-all min-[2400px]:h-16 min-[2400px]:w-16 min-[2400px]:rounded-2xl ${
                      !canUndo
                        ? 'border-colorAtemporal/30 cursor-not-allowed opacity-50'
                        : 'border-colorAtemporal bg-C/Color1 text-C/Color5 hover:bg-C/Color2'
                    }`}
                    aria-label={ui('undo')}
                  >
                    <img
                      src={deshacerIcon}
                      alt={ui('undo')}
                      className='pointer-events-none h-6 w-6 select-none min-[2400px]:h-8 min-[2400px]:w-8'
                      draggable={false}
                    />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Content
                  side='bottom'
                  align='center'
                  className='z-50 rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-C/Color5 outline outline-[0.5px] outline-C/Color3'
                >
                  {ui('undo')}
                  <Tooltip.Arrow className='fill-C/Color1' />
                </Tooltip.Content>
              </Tooltip.Root>
            </Tooltip.Provider>

            {/* Rehacer */}
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    type='button'
                    onClick={performRedo}
                    disabled={!canRedo}
                    className={`border-colorAtemporal flex h-12 w-12 items-center justify-center rounded-xl border-[1px] transition-all min-[2400px]:h-16 min-[2400px]:w-16 min-[2400px]:rounded-2xl ${
                      !canRedo
                        ? 'border-colorAtemporal/30 cursor-not-allowed opacity-50'
                        : 'border-colorAtemporal bg-C/Color1 text-C/Color5 hover:bg-C/Color2'
                    }`}
                    aria-label={ui('redo')}
                  >
                    <img
                      src={rehacerIcon}
                      alt={ui('redo')}
                      className='pointer-events-none h-6 w-6 select-none min-[2400px]:h-8 min-[2400px]:w-8'
                      draggable={false}
                    />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Content
                  side='bottom'
                  align='center'
                  className='z-50 rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-C/Color5 outline outline-[0.5px] outline-C/Color3'
                >
                  {ui('redo')}
                  <Tooltip.Arrow className='fill-C/Color1' />
                </Tooltip.Content>
              </Tooltip.Root>
            </Tooltip.Provider>

            {/* Botón de duplicar unidad */}
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    type='button'
                    onClick={handleDuplicate}
                    disabled={!canDuplicateUnit || isDuplicating}
                    className={`border-colorAtemporal flex h-12 w-12 items-center justify-center rounded-xl border-[1px] transition-all min-[2400px]:h-16 min-[2400px]:w-16 min-[2400px]:rounded-2xl ${
                      !canDuplicateUnit || isDuplicating
                        ? 'border-colorAtemporal/30 cursor-not-allowed opacity-50'
                        : 'border-colorAtemporal bg-C/Color1 text-C/Color5 hover:bg-C/Color2'
                    }`}
                    aria-label={ui('duplicateFurniture')}
                  >
                    <img
                      src={duplicarIcon}
                      alt={ui('duplicateFurniture')}
                      className='pointer-events-none h-6 w-6 select-none min-[2400px]:h-8 min-[2400px]:w-8'
                      draggable={false}
                    />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Content
                  side='bottom'
                  align='center'
                  className='z-50 rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-C/Color5 outline outline-[0.5px] outline-C/Color3'
                >
                  {ui('duplicateFurniture')}
                  <Tooltip.Arrow className='fill-C/Color1' />
                </Tooltip.Content>
              </Tooltip.Root>
            </Tooltip.Provider>

            {/* Botón de eliminar */}
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    onClick={handleDelete}
                    disabled={!hasSelectedItems}
                    className={`border-colorAtemporal flex h-12 w-12 items-center justify-center rounded-xl border-[1px] transition-all min-[2400px]:h-16 min-[2400px]:w-16 min-[2400px]:rounded-2xl ${
                      !hasSelectedItems
                        ? 'border-colorAtemporal/30 cursor-not-allowed opacity-50'
                        : 'border-colorAtemporal bg-C/Color1 text-C/Color5 hover:bg-C/Color2'
                    }`}
                    aria-label={ui('delete')}
                  >
                    <img
                      src={eliminarIcon}
                      alt={ui('delete')}
                      className='pointer-events-none h-6 w-6 select-none min-[2400px]:h-8 min-[2400px]:w-8'
                      draggable={false}
                    />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Content
                  side='bottom'
                  align='center'
                  className='z-50 rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-C/Color5 outline outline-[0.5px] outline-C/Color3'
                >
                  {ui('delete')}
                  <Tooltip.Arrow className='fill-C/Color1' />
                </Tooltip.Content>
              </Tooltip.Root>
            </Tooltip.Provider>

            {/* Botón de cotas */}
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    onClick={toggleDimensions}
                    className={`flex h-12 w-12 items-center justify-center rounded-xl border-[1px] transition-all min-[2400px]:h-16 min-[2400px]:w-16 min-[2400px]:rounded-2xl ${
                      hideDimentions3D
                        ? 'border-colorAtemporal bg-C/Color1 text-C/Color3 hover:bg-C/Color2'
                        : 'border-colorAtemporal bg-C/Color2 text-C/Color5 hover:bg-C/Color4'
                    }`}
                    aria-label={hideDimentions3D ? ui('showDimensions') : ui('hideDimensions')}
                  >
                    <img
                      src={reglaTriangularIcon}
                      alt={hideDimentions3D ? ui('showDimensions') : ui('hideDimensions')}
                      className='pointer-events-none h-6 w-6 select-none min-[2400px]:h-8 min-[2400px]:w-8'
                      draggable={false}
                    />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Content
                  side='bottom'
                  align='center'
                  className='z-50 rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-C/Color5 outline outline-[0.5px] outline-C/Color3'
                >
                  {hideDimentions3D ? ui('showDimensions') : ui('hideDimensions')}
                  <Tooltip.Arrow className='fill-C/Color1' />
                </Tooltip.Content>
              </Tooltip.Root>
            </Tooltip.Provider>
          </div>
        </div>
      </div>

      <div className='pointer-events-none fixed bottom-4 z-[40] flex justify-center left-3 right-[calc(0.5rem+clamp(240px,26vw,320px))] min-[1200px]:left-[calc(20rem+0.5rem)] min-[1200px]:right-[calc(0.5rem+clamp(240px,26vw,320px))] min-[2400px]:bottom-6'>
        <div className='pointer-events-auto mx-auto w-fit max-w-full min-w-0 px-1 min-[480px]:px-2'>
          <ArmsCushionsPanel menuData={armsMenuData} relaxMenuData={relaxMenuData} />
        </div>
      </div>
      {isPreparingBudget && <SpinnerPDFViewer />}
      <BudgetPreviewModal
        open={showBudgetPreview}
        onClose={() => setShowBudgetPreview(false)}
      />
      <ArmRestrictionNoticeModal />
    </>
  )
}
